import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useViewPreferences } from "./ViewPreferencesContext";
import { useClientContext } from "./ClientContext";
import { useDomain } from "./DomainContext";
import { useDevMode } from "./DevModeContext";

const HARD_FALLBACK_PERSONNEL_ID = "123bc4ca-6e3a-4f59-b935-aba72a000223";

// Auto-refresh threshold in milliseconds (15 minutes)
const AUTO_REFRESH_THRESHOLD_MS = 15 * 60 * 1000;

// === DATA SOURCE CONFIGURATION ===
// Set to true to use POC Baseline API Server (single efficient call)
// Set to false to use RC API endpoints (multiple calls via appDataProxy)
const USE_POC_API_SERVER = true;

// Environment configuration
export type Environment = "vero" | "staging" | "stable" | "template";
export const ENVIRONMENT_OPTIONS: { key: Environment; label: string }[] = [
  { key: "vero", label: "Vero (Copy)" },
  { key: "staging", label: "Staging (Permissions)" },
  { key: "stable", label: "Stable (Stable)" },
  { key: "template", label: "Template" }
];
// Fallback environment when domain config doesn't specify one
const FALLBACK_ENVIRONMENT: Environment = "staging";

// Personnel filter mode
export type PersonnelFilterMode = "supervisors" | "all";

// POC Baseline API Server endpoints (efficient - single call returns all data)
const SWA_DATA_OBJECT_ENDPOINT = "/api/swa/dataobject";
const SWA_PERSONNEL_ENDPOINT = "/api/swa/personnel";
const SWA_SUPERVISORS_ENDPOINT = "/api/swa/supervisors";
const SWA_ASSIGNMENTS_ENDPOINT = "/api/swa/assignments";

// Legacy RC API endpoints (kept for reference/fallback)
const PERSONNEL_ENDPOINT = "/api/poc/appdata/personnel";
const ASSIGNMENTS_ENDPOINT = "/api/poc/appdata/assignment";
const APPDATA_ASSIGNMENTS_ENDPOINT = "/api/poc/appdata/assignments";
const SUPERVISED_ENDPOINT = "/api/poc/appdata/supervised";
const COMPETENCIES_ENDPOINT = "/api/poc/appdata/competencies";
const ROOT_APPDATA_ENDPOINT = "/api/poc/appdata/root";
const FLIGHTBOOKINGS_ENDPOINT = "/api/poc/appdata/flightbookings";

type LoadingState = "idle" | "loading" | "success" | "error";

// Generic record type
type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
};

const buildDisplayName = (person: UnknownRecord): string => {
  const preferred = toStringOrNull(person["PreferredName"])?.trim();
  if (preferred) return preferred;
  const given = toStringOrNull(person["GivenNames"])?.trim() ?? "";
  const surname = toStringOrNull(person["Surname"])?.trim() ?? "";
  const full = [given, surname].filter(Boolean).join(" ").trim();
  return full || "(Unnamed)";
};

const buildFullName = (person: UnknownRecord): string => {
  const given = toStringOrNull(person["GivenNames"])?.trim() ?? "";
  const surname = toStringOrNull(person["Surname"])?.trim() ?? "";
  return [given, surname].filter(Boolean).join(" ").trim() || buildDisplayName(person);
};

// Sources, first one wins: allPersonnelRows (dev mode), the current user's rootAppData, then supervisedData.
const buildNameMap = (
  allPersonnelRows: UnknownRecord[],
  rootAppData: unknown,
  supervisedData: unknown,
  nameOf: (person: UnknownRecord) => string
): Map<string, string> => {
  const map = new Map<string, string>();

  allPersonnelRows.forEach(person => {
    const personId = toStringOrNull(person["PersonnelId"]);
    if (!personId) return;
    map.set(personId, nameOf(person));
  });

  if (rootAppData && isRecord(rootAppData)) {
    const data = rootAppData["data"] as UnknownRecord | undefined;
    if (data) {
      const personId = toStringOrNull(data["PersonnelId"]) ?? toStringOrNull(data["Id"]);
      if (personId && !map.has(personId)) {
        map.set(personId, nameOf(data));
      }
    }
  }

  if (supervisedData) {
    extractRows(supervisedData).forEach(person => {
      const personId = toStringOrNull(person["PersonnelId"]) ?? toStringOrNull(person["Id"]);
      if (!personId) return;
      if (!map.has(personId)) {
        map.set(personId, nameOf(person));
      }
    });
  }

  return map;
};

const extractRows = (value: unknown): UnknownRecord[] => {
  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }
  if (isRecord(value)) {
    const dataValue = value["data"];
    if (Array.isArray(dataValue)) {
      return dataValue.filter(isRecord);
    }
    if (isRecord(dataValue)) {
      const odataValue = dataValue["value"];
      if (Array.isArray(odataValue)) {
        return odataValue.filter(isRecord);
      }
    }
    const directOdata = value["value"];
    if (Array.isArray(directOdata)) {
      return directOdata.filter(isRecord);
    }
  }
  return [];
};

// Helper to transform assignments from POC API format to expected format
// Handles both FULL and SLIM API responses
// Note: C# uses JobID, ClientID, SiteID etc which serialize to jobID, clientID, siteID in JSON
const transformAssignmentsToExpectedFormat = (rawAssignments: UnknownRecord[]): UnknownRecord[] => {
  return rawAssignments.map((a: UnknownRecord) => {
    const job = a.job as UnknownRecord | undefined;
    const schedules = (a.schedule as UnknownRecord[]) ?? [];
    
    // Handle both slim (no client/site nested objects, just fields) and full formats
    const client = job?.client as UnknownRecord | undefined;
    const site = job?.site as UnknownRecord | undefined;
    
    return {
      // These fields may not exist in slim format
      AssignmentId: a.assignmentId,
      PersonnelId: a.personnelId,
      AssignmentStatusId: a.assignmentStatusId,
      JobId: a.jobId,
      Job: job ? {
        // Note: C# JobID -> JSON jobID (not jobId)
        JobId: job.jobID ?? job.jobId,
        JobNumber: job.jobNumber,
        ClientId: job.clientId,
        SiteId: job.siteId,
        Start: job.start,
        End: job.end,
        JobGroupId: job.jobGroupId,
        JobStatusId: job.jobStatusId,
        OwnerId: job.ownerID ?? job.ownerId,
        IsSchedulingEnabled: job.isSchedulingEnabled,
        WorkDescription: job.workDescription,
        QuoteNo: job.quoteNo,
        OrderNo: job.orderNo,
        ReqNo: job.reqNo,
        Active: job.active,
        // Client: handle both full (nested object) and slim (just clientName) formats
        Client: client ? {
          ClientId: client.clientID ?? client.clientId,
          ClientName: client.clientName,
          AccountsEmail: client.accountsEmail,
          HeadOffice: client.headOffice,
          Phone: client.phone,
          VendorNumber: client.vendorNumber,
          Active: client.active
        } : null,
        // Site: handle both full (nested object) and slim (fewer fields) formats
        Site: site ? {
          SiteId: site.siteID ?? site.siteId,
          SiteName: site.siteName,
          ClientId: site.clientID ?? site.clientId,
          Instructions: site.instructions,
          Keywords: site.keywords,
          Location: site.location,
          Active: site.active
        } : null,
        // JobStatus: may not exist in slim format
        JobStatus: job.jobStatus ? {
          JobStatusId: (job.jobStatus as UnknownRecord).jobStatusID ?? (job.jobStatus as UnknownRecord).jobStatusId,
          Status: (job.jobStatus as UnknownRecord).status,
          IsOpen: (job.jobStatus as UnknownRecord).isOpen,
          IsComplete: (job.jobStatus as UnknownRecord).isComplete,
          IsJobHidden: (job.jobStatus as UnknownRecord).isJobHidden,
          IsOngoing: (job.jobStatus as UnknownRecord).isOngoing,
          Order: (job.jobStatus as UnknownRecord).order
        } : null
      } : null,
      Schedule: schedules.map((s: UnknownRecord) => ({
        // AssignmentScheduledEventId may not exist in slim format
        AssignmentScheduledEventId: s.assignmentScheduledEventId,
        Abbr: s.abbr,
        Colour: s.colour,
        Type: s.type,
        Hours: s.hours,
        Date: s.date
      })),
      ...a
    };
  });
};

// Personnel assignment data (from API)
interface PersonnelAssignmentData {
  assignments: UnknownRecord[];
  rawJson: unknown;
}

interface DataContextValue {
  // Loading state
  loadingState: LoadingState;
  loadingProgress: string;
  error: string | null;
  
  // Current personnel ID (with fallback logic applied)
  currentPersonnelId: string;
  
  // Environment
  currentEnvironment: Environment;
  setCurrentEnvironment: (env: Environment) => void;
  
  // Personnel filter mode (supervisors or all)
  personnelFilterMode: PersonnelFilterMode;
  setPersonnelFilterMode: (mode: PersonnelFilterMode) => void;
  
  // Computed supervisor mode (true if supervised data has at least one row)
  isSupervisor: boolean;
  
  // Personnel data (filtered based on personnelFilterMode)
  personnelRows: UnknownRecord[];
  
  // All personnel (unfiltered)
  allPersonnelRows: UnknownRecord[];
  
  // Supervisors list
  supervisorsRows: UnknownRecord[];
  
  // Assignment data (from /assignment endpoint - list of all assignments)
  assignmentRows: UnknownRecord[];
  
  // Supervised competencies data (raw JSON)
  supervisedData: unknown;
  
  // Competencies data for current user (raw JSON)
  competenciesData: unknown;
  
  // Root app data for current user (raw JSON) - contains user info
  rootAppData: unknown;
  
  // Flight bookings data (raw JSON)
  flightBookingsData: unknown;
  
  // Per-personnel assignments data (from /assignments endpoint - detailed with schedules)
  personnelAssignmentsMap: Map<string, PersonnelAssignmentData>;

  // Timesheet rows exactly as the API server sent them (own and supervised people together)
  personnelTimeSheetRows: UnknownRecord[];

  // True when the last load returned at least one timesheet row
  hasTimesheets: boolean;
  
  // Personnel name map (derived from personnelRows)
  personnelNameMap: Map<string, string>;
  personnelFullNameMap: Map<string, string>;
  
  // Helper to fetch assignments for a specific personnel (uses cache or fetches)
  getPersonnelAssignments: (personnelId: string) => Promise<PersonnelAssignmentData | null>;
  
  // Refresh all data
  refreshData: () => Promise<void>;
  
  // Check if data is stale and refresh if needed (call on page navigation)
  checkAndRefreshIfStale: () => void;
  
  // Last refresh timestamp
  lastRefreshTime: number | null;
}

const DataContext = createContext<DataContextValue | null>(null);

export function useDataContext(): DataContextValue {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataContextProvider");
  }
  return context;
}

interface DataContextProviderProps {
  children: ReactNode;
}

export function DataContextProvider({ children }: DataContextProviderProps): JSX.Element {
  const { reportsPrefs } = useViewPreferences();
  const { client } = useClientContext();
  const { matchedEnvironment, isLoading: domainLoading } = useDomain();
  const { devMode } = useDevMode();
  
  // Determine the default environment from domain config, validating it's a known environment
  const defaultEnvironment = useMemo((): Environment => {
    const validEnvs: Environment[] = ENVIRONMENT_OPTIONS.map(option => option.key);
    if (validEnvs.includes(matchedEnvironment as Environment)) {
      return matchedEnvironment as Environment;
    }
    return FALLBACK_ENVIRONMENT;
  }, [matchedEnvironment]);
  
  // Get personnelId from ClientContext (set during registration), fall back to hard-coded ID for dev
  const clientPersonnelId = (client?.personMetadata as Record<string, unknown> | undefined)?.personnelId as string | undefined;
  const fallbackPersonnelId = clientPersonnelId ?? HARD_FALLBACK_PERSONNEL_ID;
  const currentPersonnelId = reportsPrefs.selectedPersonnelId ?? fallbackPersonnelId;
  const defaultPersonnelIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            currentPersonnelId,
            "df01522c-b7e4-4124-8721-7eefbedafdab",
            "257b7101-017f-4e2d-87ac-01017ae9a869",
            "29b15f30-212a-4eaf-b995-81a14a6c5fdc",
            HARD_FALLBACK_PERSONNEL_ID
          ].filter((id): id is string => Boolean(id))
        )
      ),
    [currentPersonnelId]
  );
  
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [loadingProgress, setLoadingProgress] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  
  // Environment and filter state - initialized to null until brand config loads
  const [currentEnvironment, setCurrentEnvironmentState] = useState<Environment | null>(null);
  const [environmentInitialized, setEnvironmentInitialized] = useState(false);
  
  // Initialize environment from domain config once brand loading completes
  useEffect(() => {
    if (!domainLoading && !environmentInitialized) {
      setCurrentEnvironmentState(defaultEnvironment);
      setEnvironmentInitialized(true);
    }
  }, [domainLoading, defaultEnvironment, environmentInitialized]);
  
  // Use the current environment if set, otherwise use the default from domain config
  const effectiveEnvironment = currentEnvironment ?? defaultEnvironment;
  const [personnelFilterMode, setPersonnelFilterModeState] = useState<PersonnelFilterMode>("supervisors");
  
  // Personnel lists (all and supervisors)
  const [allPersonnelRows, setAllPersonnelRows] = useState<UnknownRecord[]>([]);
  const [supervisorsRows, setSupervisorsRows] = useState<UnknownRecord[]>([]);
  
  const [assignmentRows, setAssignmentRows] = useState<UnknownRecord[]>([]);
  const [supervisedData, setSupervisedData] = useState<unknown>(null);
  const [competenciesData, setCompetenciesData] = useState<unknown>(null);
  const [rootAppData, setRootAppData] = useState<unknown>(null);
  const [flightBookingsData, setFlightBookingsData] = useState<unknown>(null);
  const [personnelAssignmentsMap, setPersonnelAssignmentsMap] = useState<Map<string, PersonnelAssignmentData>>(new Map());
  const [personnelTimeSheetRows, setPersonnelTimeSheetRows] = useState<UnknownRecord[]>([]);
  const hasTimesheets = personnelTimeSheetRows.length > 0;
  
  // Track the personnel ID that was used for the last data load
  const lastLoadedPersonnelIdRef = useRef<string | null>(null);
  
  // Track the environment that was used for the last data load
  const lastLoadedEnvironmentRef = useRef<Environment | null>(null);
  
  // Track if personnel list has been loaded for the current environment
  const personnelListLoadedForEnvRef = useRef<Environment | null>(null);
  
  // Use ref to track if a refresh is in progress to avoid duplicate refreshes
  const isRefreshingRef = useRef(false);
  
  // Compute isSupervisor from supervised data (true if at least one supervised row)
  const isSupervisor = useMemo(() => {
    if (!supervisedData) return false;
    const data = supervisedData as { data?: { value?: unknown[] } };
    const rows = data?.data?.value ?? [];
    return Array.isArray(rows) && rows.length > 0;
  }, [supervisedData]);
  
  // Derive personnelRows from filter mode
  const personnelRows = useMemo(() => {
    return personnelFilterMode === "supervisors" ? supervisorsRows : allPersonnelRows;
  }, [personnelFilterMode, supervisorsRows, allPersonnelRows]);

  // Build personnel name map from multiple sources:
  // 1. allPersonnelRows (available in dev mode)
  // 2. rootAppData (current user's data - always available)
  // 3. supervisedData (supervised people - always available)
  const personnelNameMap = useMemo(
    () => buildNameMap(allPersonnelRows, rootAppData, supervisedData, buildDisplayName),
    [allPersonnelRows, rootAppData, supervisedData]
  );

  // Given names and surname only, for the header greeting; the map above prefers the preferred name.
  const personnelFullNameMap = useMemo(
    () => buildNameMap(allPersonnelRows, rootAppData, supervisedData, buildFullName),
    [allPersonnelRows, rootAppData, supervisedData]
  );

  // === POC API SERVER DATA LOADING ===
  // Efficient single-call approach using SWADataObject endpoint
  const loadAllDataFromPocServer = useCallback(async (personnelIdToLoad: string, env: Environment, isDevMode: boolean) => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    
    setLoadingState("loading");
    setError(null);
    
    try {
      // Step 1: Load personnel lists (both all and supervisors) when environment changes
      // Only load in dev mode - non-dev mode doesn't need personnel switching
      if (isDevMode && personnelListLoadedForEnvRef.current !== env) {
        setLoadingProgress("Loading personnel lists...");
        
        // Load both lists in parallel
        const [personnelRes, supervisorsRes] = await Promise.all([
          fetch(`${SWA_PERSONNEL_ENDPOINT}?env=${encodeURIComponent(env)}`),
          fetch(`${SWA_SUPERVISORS_ENDPOINT}?env=${encodeURIComponent(env)}`)
        ]);
        
        if (!personnelRes.ok) throw new Error(`Personnel fetch failed: ${personnelRes.status}`);
        if (!supervisorsRes.ok) throw new Error(`Supervisors fetch failed: ${supervisorsRes.status}`);
        
        const [personnelJson, supervisorsJson] = await Promise.all([
          personnelRes.json(),
          supervisorsRes.json()
        ]);
        
        // Transform all personnel
        const rawPersonnelData = personnelJson?.data ?? [];
        const transformedPersonnel = Array.isArray(rawPersonnelData) 
          ? rawPersonnelData.map((p: UnknownRecord) => ({
              PersonnelId: p.personnelId ?? p.PersonnelId,
              GivenNames: p.givenName ?? p.GivenNames ?? p.givenNames,
              Surname: p.surname ?? p.Surname,
              PreferredName: p.preferredName ?? p.PreferredName,
              Email: p.email ?? p.Email,
              ...p
            }))
          : [];
        setAllPersonnelRows(transformedPersonnel);
        
        // Transform supervisors
        const rawSupervisorsData = supervisorsJson?.data ?? [];
        const transformedSupervisors = Array.isArray(rawSupervisorsData) 
          ? rawSupervisorsData.map((p: UnknownRecord) => ({
              PersonnelId: p.personnelId ?? p.PersonnelId,
              GivenNames: p.givenName ?? p.GivenNames ?? p.givenNames,
              Surname: p.surname ?? p.Surname,
              PreferredName: p.preferredName ?? p.PreferredName,
              Email: p.email ?? p.Email,
              ...p
            }))
          : [];
        setSupervisorsRows(transformedSupervisors);
        
        personnelListLoadedForEnvRef.current = env;
        console.log("[DataContext] Personnel lists loaded for env:", env, {
          allPersonnel: transformedPersonnel.length,
          supervisors: transformedSupervisors.length
        });
        
        // If no supervisors are available and we're in supervisors mode, switch to all
        if (transformedSupervisors.length === 0) {
          setPersonnelFilterModeState("all");
        }
      }
      
      // Step 2: Load the main SWA data object (contains everything for the personnel)
      setLoadingProgress("Loading data...");
      const swaRes = await fetch(`${SWA_DATA_OBJECT_ENDPOINT}?personnelId=${encodeURIComponent(personnelIdToLoad)}&env=${encodeURIComponent(env)}`);
      if (!swaRes.ok) throw new Error(`SWA data object fetch failed: ${swaRes.status}`);
      const swaJson = await swaRes.json();
      
      // The SWA response structure (slim endpoint with timing wrapper):
      // { source, personnelId, env, data: { data: { personnelId, givenName, surname, supervised, competencies, travelBookings, assignments }, timing: { ... } } }
      const swaData = swaJson?.data?.data ?? swaJson?.data ?? {};
      
      // Transform supervised data to expected format (pages expect capitalized field names)
      const supervised = swaData.supervised ?? [];
      const transformedSupervised = supervised.map((s: UnknownRecord) => {
        // Transform competencies to expected format (put ...c first so our fields override)
        const rawCompetencies = (s.competencies as UnknownRecord[]) ?? [];
        const transformedCompetencies = rawCompetencies.map((c: UnknownRecord) => ({
          ...c,
          PersonnelCompetencyId: c.personnelCompetencyId,
          CompetencyId: c.competencyId,
          Type: c.type,
          Expires: c.expires,
          Category: c.category,
          IsRole: c.isRole,
          ParentId: c.parentId
        }));
        
        // Transform travel bookings to expected format (put ...t first so our fields override)
        const rawTravelBookings = (s.travelBookings as UnknownRecord[]) ?? [];
        const transformedTravelBookings = rawTravelBookings.map((t: UnknownRecord) => {
          const flight = t.flight as UnknownRecord | undefined;
          return {
            ...t,
            FlightBookingId: t.flightBookingId,
            FlightId: t.flightId,
            Flight: flight ? {
              FlightId: flight.flightId,
              FlightNumber: flight.flightNumber,
              DepartAt: flight.departAt,
              FlightNotes: flight.flightNotes,
              DepartAirport: flight.departAirport,
              DepartTerminal: flight.departTerminal,
              ArriveAirport: flight.arriveAirport,
              ArriveTerminal: flight.arriveTerminal,
              ArriveBy: flight.arriveBy,
              Carrier: flight.carrier,
              BookingNotes: flight.bookingNotes,
              CarrierNotes: flight.carrierNotes
            } : null
          };
        });
        
        // Transform assignments to expected format
        const rawAssignments = (s.assignments as UnknownRecord[]) ?? [];
        const transformedAssignments = transformAssignmentsToExpectedFormat(rawAssignments);
        
        // Put ...s first so our transformed fields override the original lowercase versions
        return {
          ...s,
          PersonnelId: s.personnelId,
          Id: s.personnelId,
          GivenNames: s.givenName,
          GivenName: s.givenName,
          Surname: s.surname,
          PreferredName: s.preferredName,
          Email: s.email,
          Competencies: transformedCompetencies,
          TravelBookings: transformedTravelBookings,
          Flights: transformedTravelBookings, // Alias for compatibility
          Assignments: transformedAssignments
        };
      });
      
      // Build supervised data in the format expected by the app
      setSupervisedData({
        source: "POC API Server",
        personnelId: personnelIdToLoad,
        data: { value: transformedSupervised }
      });
      
      // Build competencies data (transform to expected format, put ...c first so our fields override)
      const rawCompetencies = (swaData.competencies as UnknownRecord[]) ?? [];
      const transformedCompetencies = rawCompetencies.map((c: UnknownRecord) => ({
        ...c,
        PersonnelCompetencyId: c.personnelCompetencyId,
        CompetencyId: c.competencyId,
        Type: c.type,
        Expires: c.expires,
        Category: c.category,
        IsRole: c.isRole,
        ParentId: c.parentId
      }));
      setCompetenciesData({
        source: "POC API Server",
        personnelId: personnelIdToLoad,
        data: { value: transformedCompetencies }
      });
      
      // Build root app data (personnel info + assignments)
      // Transform the root user's assignments to expected format
      const rawRootAssignments = (swaData.assignments as UnknownRecord[]) ?? [];
      const transformedRootAssignments = transformAssignmentsToExpectedFormat(rawRootAssignments);
      
      setRootAppData({
        source: "POC API Server",
        personnelId: personnelIdToLoad,
        data: {
          PersonnelId: swaData.personnelId,
          Id: swaData.personnelId,
          GivenNames: swaData.givenName,
          GivenName: swaData.givenName,
          Surname: swaData.surname,
          PreferredName: swaData.preferredName,
          Email: swaData.email,
          Assignments: transformedRootAssignments
        }
      });
      
      // Build flight bookings data (transform to expected format, put ...t first so our fields override)
      const rawTravelBookings = (swaData.travelBookings as UnknownRecord[]) ?? [];
      const transformedTravelBookings = rawTravelBookings.map((t: UnknownRecord) => {
        const flight = t.flight as UnknownRecord | undefined;
        return {
          ...t,
          FlightBookingId: t.flightBookingId,
          FlightId: t.flightId,
          Flight: flight ? {
            FlightId: flight.flightId,
            FlightNumber: flight.flightNumber,
            DepartAt: flight.departAt,
            FlightNotes: flight.flightNotes,
            DepartAirport: flight.departAirport,
            DepartTerminal: flight.departTerminal,
            ArriveAirport: flight.arriveAirport,
            ArriveTerminal: flight.arriveTerminal,
            ArriveBy: flight.arriveBy,
            Carrier: flight.carrier,
            BookingNotes: flight.bookingNotes,
            CarrierNotes: flight.carrierNotes
          } : null
        };
      });
      setFlightBookingsData({
        source: "POC API Server",
        personnelId: personnelIdToLoad,
        data: { value: transformedTravelBookings }
      });
      
      lastLoadedPersonnelIdRef.current = personnelIdToLoad;
      lastLoadedEnvironmentRef.current = env;
      
      // Step 3: Build assignments map from the SWA data
      // The SWA data includes assignments for main user and all supervised
      setLoadingProgress("Processing assignments...");
      const assignmentsMap = new Map<string, PersonnelAssignmentData>();
      
      // Main user's assignments (transform to expected format)
      const mainRawAssignments = (swaData.assignments as UnknownRecord[]) ?? [];
      const mainTransformedAssignments = transformAssignmentsToExpectedFormat(mainRawAssignments);
      assignmentsMap.set(personnelIdToLoad, {
        assignments: mainTransformedAssignments,
        rawJson: { data: { value: mainTransformedAssignments } }
      });
      
      // Supervised personnel assignments (already transformed in transformedSupervised)
      transformedSupervised.forEach((s: UnknownRecord) => {
        const pid = s.PersonnelId as string;
        const assignments = (s.Assignments as UnknownRecord[]) ?? [];
        if (pid) {
          assignmentsMap.set(pid, {
            assignments,
            rawJson: { data: { value: assignments } }
          });
        }
      });
      
      // Load assignment rows (global list) - not personnel-specific
      // This is used for the assignment status dropdown etc.
      setAssignmentRows([]);
      
      setPersonnelAssignmentsMap(assignmentsMap);

      // A server without the timesheet table, or one older than that field, sends none: no tab.
      const rawTimeSheets = Array.isArray(swaData.personnelTimeSheets) ? swaData.personnelTimeSheets : [];
      setPersonnelTimeSheetRows(
        rawTimeSheets.filter((row: unknown): row is UnknownRecord => typeof row === "object" && row !== null && !Array.isArray(row))
      );

      setLoadingProgress("");
      setLoadingState("success");
      setLastRefreshTime(Date.now());
      
      console.log("[DataContext] Loaded data from POC API Server for personnel:", personnelIdToLoad, {
        supervisedCount: supervised.length,
        competenciesCount: rawCompetencies.length,
        travelBookingsCount: rawTravelBookings.length,
        assignmentsCount: mainRawAssignments.length
      });
      
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load data";
      console.error("[DataContext] POC API Server error:", err);
      setPersonnelTimeSheetRows([]);
      setError(message);
      setLoadingState("error");
      setLoadingProgress("");
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // === LEGACY RC API DATA LOADING ===
  // Multiple-call approach using RC API proxy endpoints
  const loadAllDataFromRcApi = useCallback(async (personnelIdToLoad: string) => {
    // Prevent duplicate refreshes
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    
    setLoadingState("loading");
    setError(null);
    
    try {
      // Step 1: Load personnel and assignments in parallel
      setLoadingProgress("Loading personnel and assignments...");
      const [personnelRes, assignmentsRes] = await Promise.all([
        fetch(PERSONNEL_ENDPOINT),
        fetch(ASSIGNMENTS_ENDPOINT)
      ]);
      
      if (!personnelRes.ok) throw new Error(`Personnel fetch failed: ${personnelRes.status}`);
      if (!assignmentsRes.ok) throw new Error(`Assignments fetch failed: ${assignmentsRes.status}`);
      
      const [personnelJson, assignmentsJson] = await Promise.all([
        personnelRes.json(),
        assignmentsRes.json()
      ]);
      
      setPersonnelRows(extractRows(personnelJson));
      setAssignmentRows(extractRows(assignmentsJson));
      
      // Step 2: Load supervised competencies, competencies, root data, and flight bookings in parallel
      setLoadingProgress("Loading competencies and travel...");
      const [supervisedRes, competenciesRes, rootRes, flightBookingsRes] = await Promise.all([
        fetch(`${SUPERVISED_ENDPOINT}?personnelId=${encodeURIComponent(personnelIdToLoad)}`),
        fetch(`${COMPETENCIES_ENDPOINT}?personnelId=${encodeURIComponent(personnelIdToLoad)}`),
        fetch(`${ROOT_APPDATA_ENDPOINT}?personnelId=${encodeURIComponent(personnelIdToLoad)}`),
        fetch(`${FLIGHTBOOKINGS_ENDPOINT}?personnelId=${encodeURIComponent(personnelIdToLoad)}`)
      ]);
      
      if (!supervisedRes.ok) throw new Error(`Supervised fetch failed: ${supervisedRes.status}`);
      if (!competenciesRes.ok) throw new Error(`Competencies fetch failed: ${competenciesRes.status}`);
      if (!rootRes.ok) throw new Error(`Root data fetch failed: ${rootRes.status}`);
      if (!flightBookingsRes.ok) throw new Error(`Flight bookings fetch failed: ${flightBookingsRes.status}`);
      
      const [supervisedJson, competenciesJson, rootJson, flightBookingsJson] = await Promise.all([
        supervisedRes.json(),
        competenciesRes.json(),
        rootRes.json(),
        flightBookingsRes.json()
      ]);
      
      setSupervisedData(supervisedJson);
      setCompetenciesData(competenciesJson);
      setRootAppData(rootJson);
      setFlightBookingsData(flightBookingsJson);
      lastLoadedPersonnelIdRef.current = personnelIdToLoad;
      
      // Step 3: Load detailed assignments for default personnel
      setLoadingProgress("Loading schedules...");
      const assignmentsMap = new Map<string, PersonnelAssignmentData>();
      
      // Load all default personnel assignments in parallel
      const assignmentPromises = defaultPersonnelIds.map(async (personnelId) => {
        try {
          const url = `${APPDATA_ASSIGNMENTS_ENDPOINT}?personnelId=${encodeURIComponent(personnelId)}`;
          const res = await fetch(url);
          if (!res.ok) {
            console.warn(`Assignments fetch failed for ${personnelId}: ${res.status}`);
            return null;
          }
          const json = await res.json();
          return { personnelId, json };
        } catch (err) {
          console.warn(`Failed to fetch assignments for ${personnelId}:`, err);
          return null;
        }
      });
      
      const results = await Promise.all(assignmentPromises);
      results.forEach(result => {
        if (result) {
          assignmentsMap.set(result.personnelId, {
            assignments: extractRows(result.json),
            rawJson: result.json
          });
        }
      });
      
      setPersonnelAssignmentsMap(assignmentsMap);
      setPersonnelTimeSheetRows([]);
      setLoadingProgress("");
      setLoadingState("success");
      setLastRefreshTime(Date.now());
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load data";
      setPersonnelTimeSheetRows([]);
      setError(message);
      setLoadingState("error");
      setLoadingProgress("");
    } finally {
      isRefreshingRef.current = false;
    }
  }, [defaultPersonnelIds]);

  // Main load function - delegates to appropriate data source
  const loadAllData = useCallback(async (personnelIdToLoad: string, env: Environment, isDevMode: boolean) => {
    if (USE_POC_API_SERVER) {
      return loadAllDataFromPocServer(personnelIdToLoad, env, isDevMode);
    } else {
      return loadAllDataFromRcApi(personnelIdToLoad);
    }
  }, [loadAllDataFromPocServer, loadAllDataFromRcApi]);

  // Function to get personnel assignments (from cache or fetch)
  const getPersonnelAssignments = useCallback(async (personnelId: string): Promise<PersonnelAssignmentData | null> => {
    // Check cache first
    const cached = personnelAssignmentsMap.get(personnelId);
    if (cached) {
      return cached;
    }
    
    // Fetch if not cached
    try {
      // Use appropriate endpoint based on data source
      const url = USE_POC_API_SERVER
        ? `${SWA_ASSIGNMENTS_ENDPOINT}?personnelId=${encodeURIComponent(personnelId)}&env=${encodeURIComponent(effectiveEnvironment)}`
        : `${APPDATA_ASSIGNMENTS_ENDPOINT}?personnelId=${encodeURIComponent(personnelId)}`;
      
      const res = await fetch(url);
      if (!res.ok) {
        console.warn(`Assignments fetch failed for ${personnelId}: ${res.status}`);
        return null;
      }
      const json = await res.json();
      
      // Handle different response formats
      let assignments: UnknownRecord[];
      if (USE_POC_API_SERVER) {
        // POC API Server format: { source, personnelId, env, data: [...] }
        assignments = Array.isArray(json?.data) ? json.data : [];
      } else {
        // RC API format: uses extractRows helper
        assignments = extractRows(json);
      }
      
      const data: PersonnelAssignmentData = {
        assignments,
        rawJson: json
      };
      
      // Update cache
      setPersonnelAssignmentsMap(prev => {
        const newMap = new Map(prev);
        newMap.set(personnelId, data);
        return newMap;
      });
      
      return data;
    } catch (err) {
      console.warn(`Failed to fetch assignments for ${personnelId}:`, err);
      return null;
    }
  }, [personnelAssignmentsMap, effectiveEnvironment]);

  // Check if data is stale and refresh if needed
  const checkAndRefreshIfStale = useCallback(() => {
    if (loadingState === "loading") return; // Already loading
    if (lastRefreshTime === null) return; // Never loaded yet (initial load will handle it)
    
    const timeSinceRefresh = Date.now() - lastRefreshTime;
    if (timeSinceRefresh >= AUTO_REFRESH_THRESHOLD_MS) {
      console.log(`Data is stale (${Math.round(timeSinceRefresh / 60000)} minutes old), refreshing...`);
      loadAllData(currentPersonnelId, effectiveEnvironment, devMode);
    }
  }, [loadingState, lastRefreshTime, loadAllData, currentPersonnelId, effectiveEnvironment, devMode]);

  // Load data on mount and when personnel ID or environment changes
  useEffect(() => {
    // Don't load until environment is initialized from brand config
    if (!environmentInitialized) return;
    
    // Reload if the personnel ID or environment has changed
    if (lastLoadedPersonnelIdRef.current !== currentPersonnelId || lastLoadedEnvironmentRef.current !== effectiveEnvironment) {
      loadAllData(currentPersonnelId, effectiveEnvironment, devMode);
    }
  }, [currentPersonnelId, effectiveEnvironment, loadAllData, environmentInitialized, devMode]);

  // Create a refresh function that uses the current personnel ID and environment
  // Note: This only refreshes the actual data, not the personnel lists
  const refreshData = useCallback(() => {
    return loadAllData(currentPersonnelId, effectiveEnvironment, devMode);
  }, [loadAllData, currentPersonnelId, effectiveEnvironment, devMode]);
  
  // Environment setter with cascading update logic
  const setCurrentEnvironment = useCallback((env: Environment) => {
    // Remember current selection to try to preserve it
    const previousSelectedId = reportsPrefs.selectedPersonnelId;
    
    // Update environment state - this will trigger the useEffect to reload data
    setCurrentEnvironmentState(env);
    
    // Reset the personnel list loaded flag to force reload
    personnelListLoadedForEnvRef.current = null;
    
    // Clear assignments cache when environment changes
    setPersonnelAssignmentsMap(new Map());
    
    console.log("[DataContext] Environment changed to:", env, "- will reload data");
  }, [reportsPrefs.selectedPersonnelId]);
  
  // Personnel filter mode setter
  const setPersonnelFilterMode = useCallback((mode: PersonnelFilterMode) => {
    // Don't allow switching to supervisors if there are none
    if (mode === "supervisors" && supervisorsRows.length === 0) {
      mode = "all";
    }
    setPersonnelFilterModeState(mode);
  }, [supervisorsRows.length]);

  const value: DataContextValue = {
    loadingState,
    loadingProgress,
    error,
    currentPersonnelId,
    currentEnvironment: effectiveEnvironment,
    setCurrentEnvironment,
    personnelFilterMode,
    setPersonnelFilterMode,
    isSupervisor,
    personnelRows,
    allPersonnelRows,
    supervisorsRows,
    assignmentRows,
    supervisedData,
    competenciesData,
    rootAppData,
    flightBookingsData,
    personnelAssignmentsMap,
    personnelTimeSheetRows,
    hasTimesheets,
    personnelNameMap,
    personnelFullNameMap,
    getPersonnelAssignments,
    refreshData,
    checkAndRefreshIfStale,
    lastRefreshTime
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

